/**
 * useVautoLookToBookData Hook
 *
 * Custom hook for fetching vAuto Look to Book data for a single location.
 * Powers the Look to Book pie chart in the Sales Manager Performance card.
 *
 * Source: vAuto Appraisal Summary Report — past 60 days
 * https://profittime.vauto.app.coxautoinc.com/Va/Dashboard/Provision.aspx?tab=EnterpriseInventory
 */

import { useState, useEffect } from 'react';
import { getVautoLookToBook, VAutoLookToBookResponse } from '../services/api';

interface UseVautoLookToBookDataParams {
  locationId: number;
  enabled: boolean; // Only fetch when card is visible (NEW_VEHICLE + non-Consolidated)
}

interface UseVautoLookToBookDataResult {
  data: VAutoLookToBookResponse | null;
  loading: boolean;
  error: string | null;
}

export function useVautoLookToBookData({
  locationId,
  enabled,
}: UseVautoLookToBookDataParams): UseVautoLookToBookDataResult {
  const [data, setData] = useState<VAutoLookToBookResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getVautoLookToBook(locationId)
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setData(response.data);
        } else {
          setError(response.error || 'Failed to load Look to Book data');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load Look to Book data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [locationId, enabled]);

  return { data, loading, error };
}
